import { DETipoDocumentoEnum } from "@/shared/types/documento-electronico";
import { mapCondicionPago } from "./condicion-pago-mapper";
import { MapDEItemOptions, mapFormItemsToDEItems } from "./de-item-mapper";
import type {
    EmisorConfig,
    FacturaElectronicaFormData,
    AutofacturaFormData,
    NotaCreditoFormData,
    NotaRemisionFormData,
    DocumentoAsociadoFormData,
    BaseFormData,
    VendedorData
} from "./de-types";
import { NaturalezaReceptorEnum, TipoOperacionEnum } from "@/shared/types/contribuyente";
import { Moneda } from "@/shared/types/moneda";
import { Cotizacion } from "@/shared/types/cotizacion";

function getINatVen(vendedor: VendedorData): number | undefined {
    if (vendedor.naturaleza === NaturalezaReceptorEnum.NoContribuyente && vendedor.tipoOperacion !== TipoOperacionEnum.B2F) {
        return 1;
    } else if (vendedor.tipoOperacion === TipoOperacionEnum.B2F) {
        return 2;
    }
    return undefined;
}

// En este archivo se generan los requests que van a enviarse al backend
/*
  Helper para construir el objeto documentosAsociados
 */
function buildDocumentosAsociados(formData: DocumentoAsociadoFormData) {
    if (!formData.tipoDocumentoAsociado || formData.tipoDocumentoAsociado === "none") return undefined;

    if (formData.tipoDocumentoAsociado === "2") {
        // 2: Impreso
        return [{
            iTipDocAso: 2,
            dDesTipDocAso: "Impreso",
            dNTimDI: formData.timbradoDocImpAsociado ? parseInt(formData.timbradoDocImpAsociado) : undefined,
            dEstDocAso: formData.establecimientoDocImpAsociado,
            dPExpDocAso: formData.puntoExpedicionDocImpAsociado,
            dNumDocAso: formData.numeroDocImpAsociado,
            iTipoDocAso: formData.tipoComprobanteImpreso,
            dFecEmiDL: formData.fechaEmisionDocImpAsociado instanceof Date
                ? formData.fechaEmisionDocImpAsociado.toISOString().split('T')[0]
                : formData.fechaEmisionDocImpAsociado,
            dNumResCF: formData.numeroResolucionCF || undefined,
        }];
    }

    if (formData.tipoDocumentoAsociado === "3") {
        // 3: Constancia Electronica
        return [{
            iTipDocAso: 3,
            dDesTipDocAso: "Constancia Electrónica",
            iTipCons: formData.tipoConstancia ? parseInt(formData.tipoConstancia) : undefined,
            dNumCons: formData.numeroConstancia,
            dNumControl: formData.numeroControl,
            dNumResCF: formData.numeroResolucionCF || undefined,
        }];
    }

    // default es: Electronico (1)
    const cdc = formData.cdcDocumentoAsociado;
    if (!cdc) return undefined;

    return [{
        iTipDocAso: 1,
        dDesTipDocAso: "Electrónico",
        dCdCDERef: cdc,
        dNumResCF: formData.numeroResolucionCF || undefined,
    }];
}

/*
  Obtiene fecha hora LOCAL en formato ISO sin timezone
  Esto envía la hora de pc 
 */
function getLocalDateTime(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

/*
  Crea la estructura comun para todos los DE
 */
function createBaseDERequest(
    formData: BaseFormData,
    config: EmisorConfig,
    iTiDE: number,
    receptorId: string,
    itemOptions?: MapDEItemOptions
) {
    const deItems = mapFormItemsToDEItems(formData.items, itemOptions);

    // Hora local py
    const now = getLocalDateTime();

    return {
        iTiDE,
        dEst: config.establecimiento,
        dPunExp: config.puntoExpedicion,

        dFeEmiDE: now,
        receptorId,
        clienteId: formData.clienteId || undefined,

        iTipEmi: 1, // 1 = Normal
        dCondTiCam: 1, // 1 = Fijo (required for PYG)

        items: deItems,
        dInfAdic: formData.observaciones || undefined
    };
}

function mapPagosByMoneda(
    formData: FacturaElectronicaFormData | AutofacturaFormData,
    monedaBase: Moneda,
    monedaOperacion: Moneda | undefined,
    cotizacion: Cotizacion,
    dTiCam: number
) {
    if (!formData.pagos || !monedaOperacion) {
        return formData.pagos;
    }

    return formData.pagos.map(pago => {
        if (monedaBase.simbolo === "PYG" && monedaOperacion.simbolo !== "PYG") {
            return {
                ...pago,
                monto: pago.monto / (dTiCam ?? 1)
            };
        }
        if (monedaBase.simbolo !== "PYG") {
            if (monedaOperacion.simbolo === "PYG") {
                return {
                    ...pago,
                    monto: pago.monto * (dTiCam ?? 1)
                };
            }
            if (monedaBase.simbolo !== monedaOperacion.simbolo) {
                const cotizacionPago = cotizacion.cotizacionItems.find(c => c.monedaId === monedaOperacion.id);

                return {
                    ...pago,
                    monto: pago.monto * (cotizacionPago ? (1 / cotizacionPago.monedaBaseRelacion) : 1)
                };
            }
        }
        return pago;
    });
}

/*
  Crea la estructura comun para Factura Electronica
 */
export function buildFacturaElectronicaRequest(
    formData: FacturaElectronicaFormData,
    config: EmisorConfig,
    monedaBase: Moneda,
    monedas: Moneda[],
    cotizacion: Cotizacion,
) {
    const monedaOperacion = monedas.find(m => m.id === formData.monedaOperacionId);

    var dTiCam = monedaBase.simbolo === "PYG" ? formData.conversionGuarani ?? 1 : (1 / (formData.conversionGuarani ?? 1));
    dTiCam = parseFloat(dTiCam.toFixed(2));

    const itemOptions: MapDEItemOptions = {
        dTiCam,
        monedaBase,
        monedaOperacion,
        cotizacion
    };

    const baseRequest = createBaseDERequest(formData, config, 1, formData.clienteId, itemOptions); // iTiDE = 1

    // editar los montos de formData.pagos segun la moneda de operacion
    formData.pagos = mapPagosByMoneda(formData, monedaBase, monedaOperacion, cotizacion, dTiCam);

    const compraPublicaFactura = formData.esB2G ? {
        dModCont: formData.dModCont,
        dEntCont: formData.dEntCont ? parseInt(formData.dEntCont) : undefined,
        dAnoCont: formData.dAnoCont ? parseInt(formData.dAnoCont) : undefined,
        dSecCont: formData.dSecCont ? parseInt(formData.dSecCont) : undefined,
        dFeCodCont: formData.dFeCodCont instanceof Date
            ? formData.dFeCodCont.toISOString().split('T')[0]
            : formData.dFeCodCont ?? undefined,
    } : undefined;

    return {
        ...baseRequest,
        dTiCam,
        cMoneOpe: monedaOperacion ? monedaOperacion.simbolo : "PYG",

        facturaElectronica: {
            iIndPres: parseInt(formData.indicadorPresencia),
            compraPublicaFactura,
        },
        condicionPago: mapCondicionPago({
            condicionOperacion: formData.condicionOperacion,
            pagos: formData.pagos,
            tipoCredito: formData.tipoCredito,
            plazo: formData.plazoDias,
            numeroCuotas: formData.numeroCuotas,
            monedaOperacion: monedaOperacion,
            dTiCam
        })
    };
}

// Crea la estructura comun para Autofactura
// En autofactura: Vendedor = Contribuyente que no puede facturar
// gCamAE contiene datos del VENDEDOR
export function buildAutofacturaRequest(
    formData: AutofacturaFormData,
    config: EmisorConfig,
    emisorId: string,
    vendedor: VendedorData,
    monedaBase: Moneda,
    monedas: Moneda[],
    cotizacion: Cotizacion
) {
    const monedaOperacion = monedas.find(m => m.id === formData.monedaOperacionId);

    var dTiCam = monedaBase.simbolo === "PYG" ? formData.conversionGuarani ?? 1 : (1 / (formData.conversionGuarani ?? 1));
    dTiCam = parseFloat(dTiCam.toFixed(2));

    const itemOptions: MapDEItemOptions = {
        omitIVA: true,
        dTiCam,
        monedaBase,
        monedaOperacion,
        cotizacion
    };

    // editar los montos de formData.pagos segun la moneda de operacion
    formData.pagos = mapPagosByMoneda(formData, monedaBase, monedaOperacion, cotizacion, dTiCam);

    const baseRequest = createBaseDERequest(formData, config, 4, emisorId, itemOptions); // iTiDE = 4 receptor = emisor, omitir IVA en ítems

    return {
        ...baseRequest,
        iTImp: 4, // Para autofacturas tipo de impuesto afectado debe ser ninguno (4)
        dTiCam,
        cMoneOpe: "PYG",
        autoFactura: {
            // ID del vendedor - el backend resuelve los códigos de ubicación
            vendedorId: vendedor.id,
            // Datos del VENDEDOR 
            iNatVen: getINatVen(vendedor), // Naturaleza del vendedor
            iTipIDVen: vendedor.tipoDocumento, // Tipo de documento del vendedor
            dNumIDVen: vendedor.numeroDocumento, // RUC o documento del vendedor
            dNomVen: vendedor.razonSocial, // Nombre/Razón social del vendedor
            dEmailVen: vendedor.email,
            dDirVen: vendedor.direccion,
            dNumCasVen: vendedor.numeroCasa || 0,
            transaccionAutoFactura: {
                // Lugar de la transaccion (mismo que vendedor por defecto)
                dDirProv: vendedor.direccion,
            }
        },
        condicionPago: mapCondicionPago({
            condicionOperacion: formData.condicionOperacion,
            pagos: formData.pagos,
            tipoCredito: formData.tipoCredito,
            plazo: formData.plazoDias,
            numeroCuotas: formData.numeroCuotas,
            monedaOperacion: monedaOperacion,
            dTiCam
        }),
        documentosAsociados: buildDocumentosAsociados(formData)
    };
}

/*
  Crea la estructura para Nota de Remisión Electrónica (iTiDE = 7)
 */
export function buildNotaRemisionRequest(
    formData: NotaRemisionFormData,
    config: EmisorConfig
) {
    const baseRequest = createBaseDERequest(formData, config, DETipoDocumentoEnum.NotaRemision, formData.clienteId);
    const today = getLocalDateTime().split('T')[0]; // YYYY-MM-DD

    return {
        ...baseRequest,
        cMoneOpe: "PYG",
        dCondTiCam: 1,
        notaRemision: {
            iMotEmiNR: parseInt(formData.motivoEmisionNR),
            iRespEmiNR: parseInt(formData.responsableEmision),
            dKmR: formData.kmRecorrido,
            dFecEm: today,
        },
        transporte: {
            iTipTrans: parseInt(formData.tipoTransporte),
            iModTrans: parseInt(formData.modalidadTransporte),
            iRespEmiNR: parseInt(formData.responsableFlete),
            dIniTras: formData.fechaInicioTraslado,
            dFinTras: formData.fechaFinTraslado,
            localSalida: {
                dDirLocSal: formData.localSalida.direccion,
                dNumCasSal: formData.localSalida.numeroCasa,
                cDepSal: parseInt(formData.localSalida.departamentoCodigo),
                cCiuSal: parseInt(formData.localSalida.ciudadCodigo),
            },
            localEntrega: {
                dDirLocEnt: formData.localEntrega.direccion,
                dNumCasEnt: formData.localEntrega.numeroCasa,
                cDepEnt: parseInt(formData.localEntrega.departamentoCodigo),
                cCiuEnt: parseInt(formData.localEntrega.ciudadCodigo),
            },
            vehiculo: {
                dTiVehTras: formData.vehiculo.tipoVehiculo,
                dMarVeh: formData.vehiculo.marca,
                dTipIdenVeh: parseInt(formData.vehiculo.tipoIdentificacion),
                dNroIDVeh: formData.vehiculo.tipoIdentificacion === "1" ? formData.vehiculo.numeroId : undefined,
                dNroMatVeh: formData.vehiculo.tipoIdentificacion === "2" ? formData.vehiculo.numeroMatricula : undefined,
            },
            transportista: {
                iNatTrans: parseInt(formData.transportista.naturaleza),
                dNomTrans: formData.transportista.nombre,
                dRucTrans: formData.transportista.naturaleza === "1" ? formData.transportista.ruc : undefined,
                dDVTrans: formData.transportista.naturaleza === "1" ? formData.transportista.dv : undefined,
                iTipIDTrans: formData.transportista.naturaleza === "2" ? parseInt(formData.transportista.tipoDocumento!) : undefined,
                dNumIDTrans: formData.transportista.naturaleza === "2" ? formData.transportista.numeroDocumento : undefined,
                cNacTrans: formData.transportista.nacionalidad || undefined,
                dDesNacTrans: formData.transportista.nacionalidad ? formData.transportista.nacionalidadDescripcion || undefined : undefined,
                dDomFisc: formData.transportista.domicilioFiscal || undefined,
                dNumIDChof: formData.transportista.numeroDocChofer,
                dNomChof: formData.transportista.nombreChofer,
                dDirChof: formData.transportista.direccionChofer || undefined,
            },
        },
        documentosAsociados: buildDocumentosAsociados(formData)
    };
}

/*
  Crea la estructura comun para Nota de Credito
 */
export function buildNotaCreditoRequest(
    formData: NotaCreditoFormData,
    config: EmisorConfig
) {
    const baseRequest = createBaseDERequest(formData, config, DETipoDocumentoEnum.NotaCredito, formData.clienteId);

    return {
        ...baseRequest,
        dTiCam: formData.dTiCam,
        cMoneOpe: formData.cMoneOpe || "PYG",
        notaCreditoDebito: {
            iMotEmi: parseInt(formData.motivoEmision),
            dDesMotEmi: "Nota de crédito"
        },
        documentosAsociados: buildDocumentosAsociados(formData)
    };
}


